import React from 'react';
import { useIntl } from 'react-intl';
import Box from '@nubank/nuds-web/components/Box/Box';
import Link from '@nubank/nuds-web/components/Link/Link';
import { spacing, useNuDSTheme } from '@nubank/nuds-web/styles/themeUtils';
import PropTypes from 'prop-types';

import ConditionsSection from 'components/ConditionsSection/ConditionsSection';
import ConditionsTextBlock from 'components/ConditionsTextBlock/ConditionsTextBlock';

import { StyledLink } from '../../styles/StyledLink';
import ConditionsSectionVariant from '../../../../components/ConditionsSectionVariant/ConditionsSectionVariant';
import Contracts from '../../../CreditCardConditions/patterns/Contracts/Contracts';

const Conditions = ({ interest, conditions, contracts }) => {
  const { formatMessage } = useIntl();
  const theme = useNuDSTheme();
  const {
    variants,
  } = conditions;
  const interestRateLastUpdateProps = interest
    ? {
      textIntlKey: 'CREDIT_CARD.CONDITIONS.RATES.INTEREST_RATE_LAST_UPDATED_TITLE',
      textIntlValues: {
        ...interest,
        strong: msg => (
          <strong>{msg}</strong>
        ),
      },
    } : {
      textIntlKey: 'CREDIT_CARD.CONDITIONS.RATES.INTEREST_RATE_LAST_UPDATED_FALLBACK_TITLE',
      textIntlValues: {
        br: (
          <>
            <br />
            <br />
          </>
        ),
      },
    };
  return (
    <>
      <ConditionsSection titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.EFFICIENCY.TITLE" showImage>
        <ConditionsTextBlock
          titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.EFFICIENCY.SUBTITLE"
          titleIntlValues={{
            span: msg => (
              <span>{msg}</span>
            ),
          }}
          titleStyling={`span {color: ${theme.colors.primary.default}} `}
        />

        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.EFFICIENCY.DESCRIPTION"
        />

        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.VARIANTS.TITLE"
          textIntlValues={{
            strong: msg => (
              <strong>{msg}</strong>
            ),
          }}
          textVariant="subtitle1"
          textStyling="strong { font-weight: bold;}"
        />

        <ConditionsSectionVariant variants={variants} interest={interest} />

        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.VARIANTS.RATES.INTEREST_RATE_SUMMARY"
          textIntlValues={{
            br: (
              <>
                <br />
              </>
            ),
            strong: msg => (
              <strong>{msg}</strong>
            ),
            link: (
              <StyledLink
                intlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.VARIANTS.RATES.INTEREST_RATE_SUMMARY.LINK_VALUE"
                color="primary"
                typographyProps={{
                  variant: 'subtitle1',
                  textDecoration: 'underline',
                }}
                rel="noopener"
                target="_blank"
                href={formatMessage({ id: 'CREDIT_CARD.CONDITIONS.RATES.RATE_CAUTION_LINK_URL' })}
              />
            ),
          }}
          textStyling={`strong { font-weight: bold; } padding-top: ${spacing('12x')};`}
        />
        <ConditionsTextBlock
          {...interestRateLastUpdateProps}
          textStyling=" font-size: 16px; "
        />
      </ConditionsSection>

      <ConditionsSection titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.GENERAL.TITLE">
        <ConditionsTextBlock
          titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.ISSUANCE_TITLE"
          titleStyling={`padding-top: ${spacing('4x')};`}
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.ISSUANCE_VALUE"
          textStyling={`padding-bottom: ${spacing('4x')}; margin-bottom: 0;`}
        />

        <ConditionsTextBlock
          titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.INSURANCE_TITLE"
          titleStyling={`padding-top: ${spacing('4x')};`}
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.INSURANCE_VALUE"
          textStyling={`padding-bottom: ${spacing('4x')}; margin-bottom: 0;`}
        />

        <ConditionsTextBlock
          titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.NU_SERVICE_TITLE"
          titleStyling={`padding-top: ${spacing('4x')};`}
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.NU_SERVICE_VALUE"
          textStyling={`padding-bottom: ${spacing('4x')}; margin-bottom: 0;`}
        />

        <ConditionsTextBlock
          titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.ONE_INSTALLMENT_TITLE"
          titleStyling={`padding-top: ${spacing('4x')};`}
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.ONE_INSTALLMENT_VALUE"
          textStyling={`padding-bottom: ${spacing('4x')}; margin-bottom: 0;`}
        />

        <ConditionsTextBlock
          titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.WITHDRAWALS_TITLE"
          titleStyling={`padding-top: ${spacing('4x')};`}
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.RATES.WITHDRAWALS_VALUE"
          textStyling={`padding-bottom: ${spacing('4x')}; margin-bottom: 0;`}
        />

        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD.CONDITIONS.RATES.MASTERCARD_BENEFITS_VALUE"
          textIntlValues={{
            link: (
              <Link
                href={formatMessage({ id: 'CONDITIONS.RATES.CREDIT_CARD.MASTERCARD_BENEFITS_VALUE_LINK_URL' })}
                intlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.CREDIT_CARD.MASTERCARD_BENEFITS_VALUE_LINK"
                variant="action"
                iconProps={{ name: 'arrow-up-right', size: 'small' }}
                color="primary"
                rel="noopener"
                target="_blank"
              />
            ),
          }}
          textStyling={`padding-top: ${spacing('4x')}; padding-bottom: ${spacing('4x')}; margin-bottom: 0;`}
        />

        <Box
          backgroundColor="#EFEFEF"
          marginTop={spacing('12x')}
          paddingVertical={spacing('6x')}
          paddingHorizontal={spacing('5x')}
          borderRadius="20px"
        >
          <ConditionsTextBlock
            titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.EXISTING_USER.TITLE"
            textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.EXISTING_USER.TEXT"
          />
        </Box>

      </ConditionsSection>

      <ConditionsSection titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.FEES.TITLE">
        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.FEES.VALUE"
          textIntlValues={{
            strong: msg => (
              <strong>{msg}</strong>
            ),
          }}
        />
      </ConditionsSection>

      <ConditionsSection titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.WITHDRAWALS.TITLE">

        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.WITHDRAWALS.VALUE"
          textIntlValues={{
            br: (
              <>
                <br />
                <br />
              </>
            ),
            strong: msg => (
              <strong>{msg}</strong>
            ),
            span: msg => (
              <span>{msg}</span>
            ),
            p: msg => (
              <p>{msg}</p>
            ),
          }}
          textStyling={`span {color: ${theme.colors.primary.default}},
        strong { font-weight: bold;},
        p {font-weight: normal;}`}
        />

        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.WITHDRAWALS.COSTS"
          textIntlValues={{
            br: (
              <>
                <br />
                <br />
              </>
            ),
            strong: msg => (
              <strong>{msg}</strong>
            ),
          }}
          textStyling="padding-left: 10px; strong { font-weight: bold;}"
        />
      </ConditionsSection>

      <ConditionsSection titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.INTERNATIONAL_PURCHASES.TITLE">
        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.INTERNATIONAL_PURCHASES.FIRST_VALUE"
          textIntlValues={{
            br: (
              <>
                <br />
                <br />
              </>
            ),
            strong: msg => (
              <strong>{msg}</strong>
            ),
          }}
        />

        <ConditionsTextBlock
          textIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.INTERNATIONAL_PURCHASES.SECOND_VALUE"
          textIntlValues={{
            br: (
              <>
                <br />
                <br />
              </>
            ),
            strong: msg => (
              <strong>{msg}</strong>
            ),
          }}
        />
      </ConditionsSection>

      <ConditionsSection titleIntlKey="CREDIT_CARD_NU_CONTROL.CONDITIONS.CONTRACT.TITLE">
        <Contracts contracts={contracts} />
      </ConditionsSection>
    </>

  );
};

Conditions.defaultProps = {
  interest: null,
  contracts: null,
};

Conditions.propTypes = {
  conditions: PropTypes.shape({
    variants: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  contracts: PropTypes.arrayOf(
    PropTypes.shape({
      created_date: PropTypes.string.isRequired,
      current: PropTypes.bool.isRequired,
      url: PropTypes.string.isRequired,
      version: PropTypes.number.isRequired,
    }),
  ),
  interest: PropTypes.shape({
    month: PropTypes.string,
    rate_value: PropTypes.string,
    start_day: PropTypes.string,
    year: PropTypes.string,
  }),
};

export default Conditions;
